import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  deleteRaster,
  discoverCogs,
  fetchRasterBounds,
  registerCog,
  retryRaster,
  uploadRaster,
} from "@/lib/api";
import type { DataScope } from "@/lib/api";
import type { DiscoveredCog, Project, Raster } from "@/lib/types";
import { Banner, Button, ConfirmDialog, EmptyState, Panel, RasterStatusBadge, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import { formatDate } from "@/lib/format";

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "?";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function RasterPanel({
  scope,
  project,
  rasters,
  onChanged,
  onZoomTo,
}: {
  scope: DataScope;
  /** Only meaningful when scope.projectId is set — used to show where offline-
   *  converted imagery should be copied. A block-scoped panel has no folder of
   *  its own and simply omits that section. */
  project?: Project;
  rasters: Raster[];
  /** Called after upload, delete, or retry -- anything that means the parent
   *  should refetch the raster list. (Was "onUploaded"; renamed once this
   *  panel grew delete/retry, since it now fires for more than just uploads.) */
  onChanged: () => void;
  /** Optional: wires up "zoom to this imagery" against the sibling MapPreview. */
  onZoomTo?: (bounds: [number, number, number, number]) => void;
}) {
  const { t, lang } = useLanguage();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  // The raster awaiting confirmation. Was a window.confirm; a dialog so every
  // destructive action in the portal asks the same way.
  const [confirmingDelete, setConfirmingDelete] = useState<Raster | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Already-converted COGs an operator dropped into this project's folder on
  // the server. Project-scoped only -- the folder is per project, so a
  // block-scoped panel has no equivalent.
  const [foundCogs, setFoundCogs] = useState<DiscoveredCog[]>([]);
  const [adopting, setAdopting] = useState<string | null>(null);
  const [pathCopied, setPathCopied] = useState(false);

  async function handleCopyPath() {
    if (!project?.imagery_folder) return;
    try {
      await navigator.clipboard.writeText(project.imagery_folder);
      setPathCopied(true);
      setTimeout(() => setPathCopied(false), 1500);
    } catch {
      // Clipboard can be denied (permissions, non-HTTPS origin). The path is
      // shown as selectable text regardless, so this is a convenience only.
    }
  }

  // Only the ones still awaiting a decision. An added file is already shown as
  // Ready in the list below, so listing it twice cost vertical space that the
  // raster list needed.
  const pendingCogs = foundCogs.filter((c) => !c.already_registered);

  const refreshFoundCogs = useCallback(async () => {
    if (scope.projectId == null) return;
    try {
      setFoundCogs(await discoverCogs(scope.projectId));
    } catch {
      // Non-fatal: upload and the raster list must keep working even if this
      // listing fails, so it degrades to "no files found" rather than an error.
    }
  }, [scope.projectId]);

  // Re-scan whenever the raster list changes too, so a file stops being offered
  // once adopted and reappears if that raster is later deleted.
  useEffect(() => {
    void refreshFoundCogs();
  }, [refreshFoundCogs, rasters]);

  async function handleAdopt(cog: DiscoveredCog) {
    if (scope.projectId == null) return;
    setAdopting(cog.filename);
    setError(null);
    try {
      await registerCog(scope.projectId, cog.filename);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.rasterPanel.couldntAddFile);
    } finally {
      setAdopting(null);
    }
  }

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file) return;
      if (!/\.tiff?$/i.test(file.name)) {
        setError(t.rasterPanel.onlyTiffAccepted);
        return;
      }
      setError(null);
      setUploading(file.name);
      setUploadPercent(0);
      try {
        await uploadRaster(scope, file, setUploadPercent);
        onChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : t.rasterPanel.uploadFailed);
      } finally {
        setUploading(null);
        setUploadPercent(0);
      }
    },
    [scope, onChanged]
  );

  async function handleDelete(raster: Raster) {
    setConfirmingDelete(null);
    setBusyId(raster.id);
    setError(null);
    try {
      await deleteRaster(raster.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.rasterPanel.deleteFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function handleRetry(raster: Raster) {
    setBusyId(raster.id);
    setError(null);
    try {
      await retryRaster(raster.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.rasterPanel.retryFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function handleZoomTo(raster: Raster) {
    if (!raster.tile_url || !onZoomTo) return;

    // Prefer the footprint the API already gave us -- the worker reads it off
    // the COG and returns it on every raster, so the old unconditional
    // fetchRasterBounds() call was a network round-trip for data already in
    // hand. It also went to tile_url's own origin, so it failed for exactly
    // the cases where the tiles were unreachable anyway, reporting a
    // confusing "couldn't read from TiTiler" instead of just working.
    const {
      bounds_min_lon: minLon,
      bounds_min_lat: minLat,
      bounds_max_lon: maxLon,
      bounds_max_lat: maxLat,
    } = raster;
    if (minLon != null && minLat != null && maxLon != null && maxLat != null) {
      onZoomTo([minLon, minLat, maxLon, maxLat]);
      return;
    }

    // Fall back to asking TiTiler, for rasters converted before the bounds
    // columns existed (they are null there, not wrong).
    setBusyId(raster.id);
    setError(null);
    try {
      const bounds = await fetchRasterBounds(raster.tile_url);
      if (bounds) {
        onZoomTo(bounds);
      } else {
        setError(t.rasterPanel.couldntReadLocation);
      }
    } catch {
      setError(t.rasterPanel.couldntReadLocation);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Panel
      title={t.rasterPanel.panelTitle(rasters.length)}
      className="h-full min-h-0"
      bodyClassName="flex flex-col min-h-0"
    >
      <div className="flex-none p-3">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            void handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
          className={clsx(
            "cursor-pointer rounded border border-dashed px-4 py-4 text-center transition",
            dragActive
              ? "border-accent bg-accent-subtle"
              : "border-line-strong hover:border-ink-muted hover:bg-surface-sunken"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".tif,.tiff"
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <p className="text-xs text-ink-secondary">
            {uploading ? t.rasterPanel.uploadingFile(uploading) : t.rasterPanel.dropHint}
          </p>
          {/* Real byte-level upload progress. An orthomosaic is routinely
              several GB, so without this the panel sat on a motionless
              "Uploading …" for minutes and looked hung. */}
          {uploading && (
            <div className="mt-2">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className="h-full rounded-full bg-accent transition-[width] duration-200 ease-out"
                  style={{ width: `${uploadPercent}%` }}
                />
              </div>
              <p className="mt-0.5 text-2xs text-ink-muted">
                {uploadPercent >= 99.5
                  ? t.rasterPanel.transferComplete
                  : t.rasterPanel.percentUploaded(Math.round(uploadPercent))}
              </p>
            </div>
          )}
          {!uploading && (
            <p className="mt-0.5 text-2xs text-ink-muted">
              {t.rasterPanel.convertedHint}
            </p>
          )}
        </div>

        {error && (
          <div className="mt-2">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        {/* Converted COGs found on the server, for imagery converted offline
            rather than uploaded.

            Only files that still need a decision are listed. An already-added
            one appears in the raster list immediately below as Ready, so
            repeating it here was duplicate information that pushed that list --
            the actual content of this panel -- out of view. In the steady state
            (everything added) this section disappears entirely. */}
        {/* Where to copy imagery converted outside the portal. Always shown for
            a project (not only when files are already present), because the
            folder is named by UUID -- if the path is not visible there is no way
            to find it, which is exactly why a converted COG ended up loose in
            storage/ instead.

            ONE line, not two. This is a reference value, consulted once when
            someone copies a file in and ignored on every other visit, and at two
            lines plus a heading it was taking a fifth of the panel from the
            raster list -- the thing the panel is actually for. The prose that
            used to explain it ("Converted elsewhere? Copy the _cog.tif here")
            is now the label plus the button, which say the same thing in the
            space of neither. The full path stays reachable by tooltip and by
            the Copy button, so truncation costs nothing. */}
        {scope.projectId && project?.imagery_folder && (
          <div className="mt-2 flex items-center gap-2 rounded border border-line bg-surface-sunken px-2 py-1">
            <span className="flex-none text-2xs text-ink-muted">
              {t.rasterPanel.cogTifArrow}
            </span>
            <p
              className="min-w-0 flex-1 truncate font-mono text-2xs text-ink-muted"
              title={project.imagery_folder}
            >
              {project.imagery_folder}
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="flex-none"
              onClick={() => void handleCopyPath()}
              title={t.rasterPanel.copyFolderPath}
            >
              {pathCopied ? t.common.copied : t.common.copy}
            </Button>
          </div>
        )}

        {scope.projectId && pendingCogs.length > 0 && (
          <div className="mt-2 rounded border border-line bg-surface-sunken p-2">
            <p className="text-2xs text-ink-muted">
              {t.rasterPanel.foundOnServer}
            </p>

            <ul className="mt-1.5 space-y-1">
              {pendingCogs.map((cog) => (
                <li
                  key={cog.filename}
                  className="rounded bg-surface-raised px-2 py-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-2xs font-medium text-ink" title={cog.filename}>
                        {cog.filename}
                      </p>
                      <p className="text-2xs text-ink-muted">
                        {formatBytes(cog.size_bytes)}
                        {cog.width && cog.height ? ` · ${cog.width}×${cog.height}` : ""}
                        {cog.crs ? ` · ${cog.crs}` : ""}
                        {cog.compression ? ` · ${cog.compression}` : ""}
                      </p>
                    </div>
                    {cog.adoptable ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={adopting !== null}
                        onClick={() => void handleAdopt(cog)}
                      >
                        {adopting === cog.filename ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          t.common.add
                        )}
                      </Button>
                    ) : (
                      <span className="flex-none text-2xs text-status-text-critical">
                        {t.rasterPanel.unusable}
                      </span>
                    )}
                  </div>
                  {/* Why a file was rejected, rather than hiding it -- the fix
                      (re-convert, finish copying) depends on the reason. */}
                  {cog.problem && (
                    <p
                      className="mt-1 line-clamp-2 text-2xs text-status-text-critical"
                      title={cog.problem}
                    >
                      {cog.problem}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* min-h-0 flex-1 overflow-y-auto here only actually scrolls if this
          component's own parent hands it a bounded height -- see the h-full
          wrappers around <RasterPanel> in the pages that render it.

          basis-[7rem] gives the list a floor. Everything above it in this panel
          (dropzone, upload progress, error banner, discovered files) is
          flex-none, so as those grew they took height from this list until a
          raster was clipped mid-row with no scrollbar to reveal it -- the list
          shrank instead of scrolling. A flex-basis means it starts from a
          usable height and scrolls internally rather than collapsing. */}
      <div className="thin-scroll min-h-0 flex-1 basis-[7rem] overflow-y-auto px-3 pb-3">
        {rasters.length === 0 ? (
          <EmptyState
            title={t.rasterPanel.noImageryYetTitle}
            body={t.rasterPanel.noImageryYetBody}
          />
        ) : (
          <ul className="space-y-1.5">
            {rasters.map((raster) => {
              const busy = busyId === raster.id;
              return (
                <li
                  key={raster.id}
                  className="rounded bg-surface-raised px-3 py-2 shadow-ring"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 flex-1 truncate text-xs font-medium text-ink">
                      {raster.original_filename}
                    </p>
                    {/* file_missing overrides the stored status: the row still
                        says "ready", but the file behind it is gone, so showing
                        a green Ready badge would be actively misleading. */}
                    {raster.file_missing ? (
                      <span className="flex-none rounded bg-status-critical-subtle px-1.5 py-0.5 text-2xs font-medium text-status-text-critical">
                        {t.rasterPanel.fileMissing}
                      </span>
                    ) : (
                      <RasterStatusBadge status={raster.status} />
                    )}
                  </div>
                  {raster.file_missing && (
                    <p className="mt-1 text-2xs text-status-text-critical">
                      {t.rasterPanel.fileMissingBody}
                    </p>
                  )}
                  <div className="mt-0.5 flex items-center gap-2 text-2xs text-ink-muted">
                    <span>{raster.block_id ? t.rasterPanel.blockSpecific : t.rasterPanel.projectWide}</span>
                    <span aria-hidden="true">·</span>
                    <span>
                      {formatDate(raster.created_at, lang, {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                  </div>
                  {raster.status === "processing" && (
                    <div className="mt-1.5">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
                        <div
                          className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
                          style={{
                            width: `${raster.progress_percent ?? 0}%`,
                          }}
                        />
                      </div>
                      {/* An estimate from the growing output file's size, not
                          exact per-block progress (rio-cogeo's own progress
                          hook only fires once, at the very end) -- shown as
                          text too so it isn't color-only, and so it's clear
                          this is approximate on a large file that can sit at
                          one number for a while. */}
                      <p className="mt-0.5 text-2xs text-ink-muted">
                        {raster.progress_percent != null
                          ? t.rasterPanel.approxConverted(Math.round(raster.progress_percent))
                          : t.rasterPanel.converting}
                      </p>
                    </div>
                  )}
                  {raster.status === "failed" && raster.error_message && (
                    <p
                      className="mt-1 line-clamp-2 text-2xs text-status-text-critical"
                      title={raster.error_message}
                    >
                      {raster.error_message}
                    </p>
                  )}

                  <div className="mt-2 flex items-center justify-end gap-1.5 border-t border-line pt-1.5">
                    {busy && <Spinner className="mr-1 h-3.5 w-3.5" />}
                    {raster.status === "ready" && raster.tile_url && onZoomTo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void handleZoomTo(raster)}
                      >
                        {t.rasterPanel.zoomTo}
                      </Button>
                    )}
                    {raster.status === "failed" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => void handleRetry(raster)}
                      >
                        {t.rasterPanel.retry}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => setConfirmingDelete(raster)}
                    >
                      {t.common.delete}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ConfirmDialog
        open={confirmingDelete !== null}
        title={t.rasterPanel.deleteThisImageryTitle}
        body={
          <>
            <span className="font-medium text-ink">
              {confirmingDelete?.original_filename}
            </span>{" "}
            {t.rasterPanel.deleteImageryBodySuffix}
          </>
        }
        confirmLabel={t.rasterPanel.deleteImageryConfirmLabel}
        busy={busyId === confirmingDelete?.id}
        onCancel={() => setConfirmingDelete(null)}
        onConfirm={() => confirmingDelete && void handleDelete(confirmingDelete)}
      />
    </Panel>
  );
}
